import React, { useState } from 'react'
import { Outlet } from 'react-router-dom'
import Sidebar from './Sidebar'
import { SuperAdminSidebar } from './SuperAdminSidebar'
import TopBar from './TopBar'
import MobileMenuToggle from './MobileMenuToggle'
import MobileHeader from '../mobile/MobileHeader'
import BottomNav from './BottomNav'
import AdminMobileBottomNav from '../admin/AdminMobileBottomNav'
import AdminMobileMenuSheet from '../admin/AdminMobileMenuSheet'
import AIAssistant from '../ai/AIAssistant'
import { useAuth } from '../../contexts/AuthContext'
import { useSubscriptionFeatures } from '../../hooks/useSubscriptionFeatures'
import { useMobile } from '../../hooks/useMobile'
import { useAI } from '../../hooks/useAI'
import SetupWizardGuard from '../SetupWizardGuard'
import PersonalSetupGuard from '../PersonalSetupGuard'
import DemoBanner from '../DemoBanner'
import DemoTour from '../demo/DemoTour'
import { isDemoUser } from '../../lib/demo'

interface LayoutProps {
  children?: React.ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const { profile, user, isSuperAdmin, isOrganizationAdmin, isOrganizationStaff, organizationRole } = useAuth()
  const { isTrainerMode } = useSubscriptionFeatures()
  const { isMobile, isTablet, width } = useMobile()
  const { setIsOpen: setAIOpen } = useAI()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    const saved = localStorage.getItem('sidebar-collapsed')
    return saved === 'true'
  })
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false)
  const isDemo = isDemoUser(user?.email || profile?.email)

  // Show BottomNav only on mobile (< 640px) per MOBILE_PATTERNS.md
  const showBottomNav = width < 640

  // In Coach Mode, AI panel is always visible (as sidebar on desktop, bottom sheet on mobile)
  const showCoachAIPanel = isTrainerMode && !isSuperAdmin

  // Open AI panel by default in Coach Mode on desktop
  React.useEffect(() => {
    if (showCoachAIPanel && !isMobile && !isTablet) {
      setAIOpen(true)
    }
  }, [showCoachAIPanel, isMobile, isTablet, setAIOpen])

  // Determine portal type based on user role
  const getPortalClass = () => {
    if (isSuperAdmin || isOrganizationAdmin) return 'admin-portal'
    if (isTrainerMode) return 'trainer-portal'
    if (isOrganizationStaff) return 'staff-portal'
    return 'admin-portal' // Default fallback
  }
  
  const portalClass = getPortalClass()
  const sidebarClass = `${portalClass.split('-')[0]}-sidebar`
  const isAdminPortal = portalClass === 'admin-portal' || portalClass === 'staff-portal' || portalClass === 'trainer-portal'
  const showMobileAdminNav = (isMobile || isTablet) && isAdminPortal && !isSuperAdmin

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }
  
  const toggleSidebarCollapse = () => {
    const newState = !isSidebarCollapsed
    setIsSidebarCollapsed(newState)
    localStorage.setItem('sidebar-collapsed', newState.toString())
  }
  
  return (
    <SetupWizardGuard>
      <DemoBanner />
      {isDemo && !showBottomNav && <DemoTour variant="admin" enabled={isDemo} />}
      <PersonalSetupGuard>
      <div className={`dashboard-container ${portalClass} ${isSidebarOpen ? 'sidebar-open' : ''} ${isSidebarCollapsed ? 'sidebar-collapsed' : ''} ${showCoachAIPanel ? 'coach-ai-mode' : ''}`} style={isDemo ? { paddingTop: 44 } : undefined}>
        {/* Spotlight Gradient Effect */}
        <div className="spotlight" aria-hidden="true" />

        {/* Mobile Menu Toggle */}
        <MobileMenuToggle isOpen={isSidebarOpen} onToggle={toggleSidebar} />

        {/* Sidebar Navigation - Hide on mobile when BottomNav is visible */}
        {!showBottomNav && (
          isSuperAdmin ? (
            <SuperAdminSidebar
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={toggleSidebarCollapse}
            />
          ) : (
            <Sidebar
              className={sidebarClass}
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={toggleSidebarCollapse}
            />
          )
        )}

        {/* Main Content Area */}
        <div className="main-content">
          {/* Top Bar - Hide on mobile/tablet */}
          {!isMobile && !isTablet && <TopBar />}

          {/* Mobile Header with back button */}
          {(isMobile || isTablet) && <MobileHeader />}

          {/* Content Container */}
          <div className="content-container">
            <div className="content-wrapper">
              {children || <Outlet />}
            </div>
          </div>
        </div>

        {/* Coach Mode AI Panel - Desktop: always visible sidebar, Mobile: bottom sheet */}
        {showCoachAIPanel && (
          <div className="coach-ai-panel">
            <AIAssistant />
          </div>
        )}

        {/* Mobile Bottom Navigation with FAB - Only on mobile (< 640px) */}
        {showBottomNav && !isSuperAdmin && (
          <BottomNav
            onMenuOpen={() => setIsAdminMenuOpen(true)}
            onFabClick={() => {
              // FAB quick action - can be customized
              // For now, opens the menu
              setIsAdminMenuOpen(true)
            }}
          />
        )}

        {/* Admin Mobile Bottom Navigation - For tablet and larger mobile */}
        {showMobileAdminNav && !showBottomNav && (
          <AdminMobileBottomNav onMenuOpen={() => setIsAdminMenuOpen(true)} />
        )}

        {/* Admin Mobile Menu Sheet - Works with both BottomNav and AdminMobileBottomNav */}
        {(showMobileAdminNav || (showBottomNav && !isSuperAdmin)) && (
          <AdminMobileMenuSheet
            isOpen={isAdminMenuOpen}
            onClose={() => setIsAdminMenuOpen(false)}
          />
        )}
      </div>
      </PersonalSetupGuard>
    </SetupWizardGuard>
  )
}